import { useRef, useState, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { fileUrl } from "../lib/api";

function fmt(t) {
  if (!isFinite(t) || t < 0) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export function AudioPlayer({ src, testId = "audio-player", compact = false }) {
  const url = fileUrl(src);
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onLoad = () => setDuration(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoad);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoad);
      a.removeEventListener("ended", onEnd);
    };
  }, [url]);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  };

  const seek = (e) => {
    const a = ref.current;
    if (!a || !duration) return;
    a.currentTime = (e.target.value / 100) * duration;
    setCurrent(a.currentTime);
  };

  const onVol = (e) => {
    const v = Number(e.target.value) / 100;
    setVolume(v);
    setMuted(v === 0);
    if (ref.current) ref.current.volume = v;
  };

  const toggleMute = () => {
    const a = ref.current;
    if (!a) return;
    a.muted = !a.muted;
    setMuted(a.muted);
  };

  return (
    <div data-testid={testId} className={`w-full ${compact ? "p-2" : "p-3"} rounded-xl bg-zinc-900/80 border border-zinc-800 flex items-center gap-3`}>
      <audio ref={ref} src={url} preload="metadata" />
      <button type="button" onClick={toggle} data-testid={`${testId}-toggle`}
        className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-violet-500 text-zinc-950 flex items-center justify-center shrink-0 hover:brightness-110">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <input type="range" min="0" max="100" value={duration ? (current / duration) * 100 : 0}
          onChange={seek} data-testid={`${testId}-seek`}
          className="w-full h-1 accent-cyan-400 cursor-pointer" />
        <div className="mt-1 flex justify-between text-[10px] tracking-wider text-zinc-500 font-mono">
          <span>{fmt(current)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>
      <button type="button" onClick={toggleMute} className="text-zinc-400 hover:text-cyan-400 shrink-0" data-testid={`${testId}-mute`}>
        {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
      <input type="range" min="0" max="100" value={muted ? 0 : volume * 100} onChange={onVol}
        data-testid={`${testId}-volume`}
        className="w-16 h-1 accent-cyan-400 hidden sm:block cursor-pointer" />
    </div>
  );
}
